import React, { useRef, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSanitize from "rehype-sanitize";
import "katex/dist/katex.min.css";

import icon from "../media/Study-Budy-Logo.png";
import "../Style/Chatbot.css";

function Chatbot({ userDetails }) {
  const currentUser = userDetails.uid;

  const [userInput, setUserInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [deviceType, setDeviceType] = useState("desktop");

  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem(`studyBuddyChat_${currentUser}`);
    return saved ? JSON.parse(saved) : [];
  });

  const bottomRef = useRef(null);
  const messageContainerRef = useRef(null);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const detectDeviceType = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent.toLowerCase();

      const isTabletUA =
        /ipad|tablet|playbook|silk/.test(userAgent) ||
        (userAgent.includes("android") && !userAgent.includes("mobile"));

      if (width <= 640) {
        setDeviceType("mobile");
      } else if (width <= 1024 || isTabletUA) {
        setDeviceType("tablet");
      } else {
        setDeviceType("desktop");
      }
    };

    detectDeviceType();
    window.addEventListener("resize", detectDeviceType);

    return () => window.removeEventListener("resize", detectDeviceType);
  }, []);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    localStorage.setItem(
      `studyBuddyChat_${currentUser}`,
      JSON.stringify(messages)
    );
  }, [messages, currentUser]);

  const createMessageId = () => {
    return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  };

  const normalizeMessage = (message, index) => {
    return {
      id: message.id || `legacy_${index}`,
      text: message.text || "",
      isUser: Boolean(message.isUser),
      type: message.type || "markdown",
      replyTo: message.replyTo || null,
      createdAt: message.createdAt || null
    };
  };

  const getMessagePreview = (message) => {
    if (!message) return "";

    const text = message.text || "";
    return text.length > 120 ? `${text.slice(0, 120)}...` : text;
  };

  const detectMessageContentType = (text = "") => {
    const hasCodeFence = /```[\s\S]*?```/.test(text);
    const hasInlineCode = /`[^`]+`/.test(text);
    const hasMathBlock = /\$\$[\s\S]*?\$\$/.test(text);
    const hasInlineMath = /(^|[^\\])\$[^$\n]+\$/.test(text);
    const hasLatexSyntax =
      /\\\(|\\\[|\\frac|\\sum|\\int|\\sqrt|\\begin\{/.test(text);

    if (hasCodeFence || hasInlineCode) return "code";
    if (hasMathBlock || hasInlineMath || hasLatexSyntax) return "math";

    return "plain";
  };

  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleReplyClick = (message) => {
    setReplyTo({
      id: message.id,
      text: message.text,
      isUser: message.isUser
    });
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const handleSubmit = async () => {
    const trimmedInput = userInput.trim();

    if (!trimmedInput || isLoading) return;

    const replyPayload = replyTo
      ? {
          id: replyTo.id,
          text: replyTo.text,
          isUser: replyTo.isUser
        }
      : null;

    try {
      const userMessage = {
        id: createMessageId(),
        text: trimmedInput,
        isUser: true,
        type: detectMessageContentType(trimmedInput),
        replyTo: replyPayload,
        createdAt: new Date().toISOString()
      };

      setMessages((prev) => [...prev, userMessage]);
      setUserInput("");
      setReplyTo(null);
      setIsLoading(true);

      const botResponse = await sendGptMessage(trimmedInput, replyPayload);

      const botText =
        botResponse?.res?.[0]?.text ||
        "Sorry, I could not generate a response.";

      const botMessage = {
        id: createMessageId(),
        text: botText,
        isUser: false,
        type: detectMessageContentType(botText),
        replyTo: replyPayload,
        createdAt: new Date().toISOString()
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.log("handleSubmit: error");
      console.log(err);

      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          text: "Sorry, something went wrong. Please try again.",
          isUser: false,
          type: "plain",
          createdAt: new Date().toISOString()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.log("Copy failed", err);
    }
  };

  const renderReplyPreview = (message) => {
    if (!message.replyTo) return null;

    return (
      <div className="reply-preview">
        <div className="reply-preview-author">
          {message.replyTo.isUser ? "You" : "Study Buddy"}
        </div>
        <div className="reply-preview-text">
          {getMessagePreview(message.replyTo)}
        </div>
      </div>
    );
  };

  const renderMessageContent = (message) => {
    if (message.isUser) {
      return <p className="message-text">{message.text}</p>;
    }

    return (
      <div
        className={`markdown-message markdown-message-${message.type || "plain"}`}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex, rehypeSanitize]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),

            table: ({ node, ...props }) => (
              <div className="table-wrapper">
                <table {...props} />
              </div>
            ),

            code: ({ inline, className, children, ...props }) => {
              const codeText = String(children).replace(/\n$/, "");
              const language = /language-(\w+)/.exec(className || "")?.[1];

              if (inline) {
                return (
                  <code className="inline-code" {...props}>
                    {children}
                  </code>
                );
              }

              return (
                <div className="code-shell">
                  <div className="code-toolbar">
                    <span>{language || "code"}</span>

                    <button
                      type="button"
                      className="copy-code-button"
                      onClick={() => copyToClipboard(codeText)}
                    >
                      Copy
                    </button>
                  </div>

                  <pre className="code-block">
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                </div>
              );
            }
          }}
        >
          {message.text}
        </ReactMarkdown>
      </div>
    );
  };

  const renderMessages = () => {
    return (
      <>
        {messages.map((rawMessage, index) => {
          const message = normalizeMessage(rawMessage, index);

          return (
            <div
              key={message.id}
              className={`message-row ${
                message.isUser ? "message-row-user" : "message-row-bot"
              }`}
            >
              <div
                className={`chat-message ${
                  message.isUser ? "user-message" : "bot-message"
                }`}
              >
                {renderReplyPreview(message)}

                {renderMessageContent(message)}

                <div className="message-actions">
                  <button
                    type="button"
                    className="reply-button"
                    onClick={() => handleReplyClick(message)}
                  >
                    Reply
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {isLoading && (
          <div className="message-row message-row-bot">
            <div className="chat-message bot-message typing-message">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={bottomRef}></div>
      </>
    );
  };

  const sendGptMessage = async (message, replyContext = null) => {
    try {
      const gptUrl = "https://gpt-backend-4dh4scdo7q-uc.a.run.app/gpt/test";

      const res = await fetch(gptUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message,
          uid: currentUser,
          replyTo: replyContext
        })
      });

      if (!res.ok) {
        throw new Error(`GPT request failed with status ${res.status}`);
      }

      const data = await res.json();
      return data;
    } catch (err) {
      console.log("sendGptMessage: error");
      console.dir(err, { depth: null });
      throw err;
    }
  };

  return (
    <div className={`chatbot chatbot-${deviceType} ${isOpen ? "open" : ""}`}>
      <div className="chatbot-info">
        <div className="chatbot-title-row">
          <img src={icon} alt="Study Buddy icon" className="chatbot-logo" />

          <div>
            <h2>Study Buddy</h2>
            <p>
              Your AI personal assistant for questions, code, maths, and study
              help.
            </p>
          </div>
        </div>

        <span className="device-pill">{deviceType}</span>
      </div>

      <div className="message-container" ref={messageContainerRef}>
        {renderMessages()}
      </div>

      {replyTo && (
        <div className="active-reply-bar">
          <div>
            <strong>
              Replying to {replyTo.isUser ? "your message" : "Study Buddy"}
            </strong>
            <p>{getMessagePreview(replyTo)}</p>
          </div>

          <button type="button" onClick={cancelReply}>
            ×
          </button>
        </div>
      )}

      <div className="input-container">
        <textarea
          className="chatbot-input"
          placeholder="Type your message..."
          value={userInput}
          onChange={handleInputChange}
          disabled={isLoading}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        <button
          className="chatbot-button"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
}

export default Chatbot;